import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  articleData?: Article;
  faqData?: { question: string; answer: string }[];
  breadcrumbs?: { name: string; url: string }[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = "website",
  articleData,
  faqData,
  breadcrumbs,
}: SEOHeadProps) {
  const location = useLocation();
  const siteUrl = typeof window !== "undefined" ? window.location.origin + __BASE_PATH__ : "";
  const fullUrl = canonicalUrl || siteUrl + location.pathname;

  useEffect(() => {
    document.title = title;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) metaDescription.setAttribute("content", description);
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords && keywords) metaKeywords.setAttribute("content", keywords);

    const updateMeta = (property: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    updateMeta("og:title", title, true);
    updateMeta("og:description", description, true);
    updateMeta("og:url", fullUrl, true);
    updateMeta("og:type", ogType, true);
    if (ogImage) updateMeta("og:image", ogImage, true);

    updateMeta("twitter:card", "summary_large_image");
    updateMeta("twitter:title", title);
    updateMeta("twitter:description", description);
    if (ogImage) updateMeta("twitter:image", ogImage);

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute("href", fullUrl);

    const existingJsonLd = document.querySelectorAll('script[type="application/ld+json"]');
    existingJsonLd.forEach((el) => el.remove());

    const addJsonLd = (data: Record<string, unknown>) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(data, null, 2);
      document.head.appendChild(script);
    };

    addJsonLd({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "K-Stream 解析",
      url: siteUrl,
      description: "深度解析韩国女主播的直播风格、内容特色与粉丝运营技巧",
      inLanguage: "zh-CN",
    });

    if (breadcrumbs && breadcrumbs.length > 0) {
      addJsonLd({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      });
    }

    if (articleData) {
      addJsonLd({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: articleData.title,
        description: articleData.excerpt,
        author: { "@type": "Organization", name: articleData.author },
        datePublished: articleData.date,
        image: articleData.imageUrl,
        publisher: { "@type": "Organization", name: "K-Stream 解析", url: siteUrl },
      });
    }

    if (faqData && faqData.length > 0) {
      addJsonLd({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqData.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      });
    }

    addJsonLd({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description: description,
      url: fullUrl,
      isPartOf: { "@type": "WebSite", name: "K-Stream 解析", url: siteUrl },
    });
  }, [title, description, keywords, fullUrl, ogImage, ogType, articleData, faqData, breadcrumbs, siteUrl]);

  return null;
}